import { ticks } from './config';
import {
  ABDUCTOR,
  AIR_STRIKE,
  ALIEN_ATTACK,
  ATOM_BOMB,
  BEEHIVE,
  BERSERKER,
  BUZZSAW,
  baseGun,
  CONE_LOBBER,
  CORN_RAIL,
  CUPID_BOW,
  DUBSTEP,
  DUCKS,
  Explosive,
  FIRE_BOW,
  FIREFLY,
  FIREWORK,
  FROSTBITE,
  GRIMOIRE,
  HAMMER,
  type KnockDef,
  LIGHTSABER,
  LONGSTEM,
  MECHA,
  MONSTER,
  POISON_DART,
  POLLINATOR,
  PRUNER,
  RC_TRACTOR,
  ROOT_ROCKET,
  SCYTHE,
  SHIELD,
  SHOTGUN,
  SHURIKEN,
  SLUDGE,
  SUNBEAM,
  TANK,
  THORNFIRE,
  TORNADO,
  TRIDENT,
  UNICORN,
  VINE_FIST,
  WAND,
  WEED_BURNER,
  WHACKER,
  weaponById,
} from './weapons';

/**
 * Gold (ADR-0009, ADR-0013): Zombie Nights' purse (its builds and towers are bought with it, ADR-0098). Earned from kills
 * (every player who damaged a minion gets its full bounty; the rest of its foes still get `minionShare` of it), by doing
 * the most damage to a mini-boss and by killing enemy players.
 */
export const GOLD = {
  /** Every player's purse as they come in (Zombie Nights: `startGold`). */
  start: 75,
  /**
   * Zombie Nights' purse (ADR-0180): six digits, the build bar's purse readout at its widest. A long run's gold and
   * Chomper's paybacks fit; past it gold is lost.
   */
  max: 999_999,
  /** A player's purse can't hold more in every other mode: gold past it is lost, so a team can't bank for a Mega rush. */
  cap: 1500,
  /** A minion's bounty (the range's sparring pen), a mega one's. */
  minion: 10,
  megaMinion: 60,
  /** Fraction of a dead minion's bounty paid to every player of the other team who didn't hit it. */
  minionShare: 0.5,
  /** Last hit on an enemy player (ADR-0140: was 30). */
  playerKill: 50,
  /** Each assist on an enemy player's death (ADR-0140: hurt them within `KILL_CREDIT.assistTicks`, a mate got the kill). */
  assist: 20,
} as const;

/**
 * Kill credit (ADR-0084): a player finished off by no player (a minion, a tower, a creep, a boss, a wall slam) is still
 * the kill of the enemy who hurt them last within `creditTicks` (they get its gold too); everyone else on the killer's
 * team who hurt them within `assistTicks` gets an assist.
 */
export const KILL_CREDIT = {
  creditTicks: ticks(5),
  assistTicks: ticks(5),
} as const;

/** A mega minion (the range's `w`): slow, huge, much tougher than a minion (multipliers). */
export const MEGA_MINION = {
  hp: 6,
  damage: 1.5,
  speed: 0.75,
  bodyRadius: 7,
  hurtRadius: 11,
} as const;

/**
 * Neutral mini-bosses: the range's zoo (`M` dens, ADR-0100) and a battle royale's altar guardians (ADR-0202). A boss goes
 * for whoever comes close or shoots it and walks home healing when pulled beyond its leash. A real fight: ADR-0031 made
 * them 1.5× as tough as ADR-0011's quick kills and hit ~30% harder. The gold goes to the player who lands the last hit
 * (ADR-0026).
 */
export const BOSS = {
  /** HP regained per tick while it has nobody to fight (fraction of max). */
  regenPerTick: 0.004,
  /** Walking home longer than this: it is back (slipped round the hedge it was wedged on; healed by then anyway). */
  returnTicks: ticks(8),
  /** Reward for the top damager. */
  gold: 150,
} as const;

export const BossKind = { Pumpkin: 0, Snapdragon: 1, Beetle: 2 } as const;
export type BossKind = (typeof BossKind)[keyof typeof BossKind];

/** What every boss def has (the kinds add their own attack numbers). */
export interface BossBase {
  readonly kind: BossKind;
  readonly name: string;
  readonly maxHp: number;
  /** px/s (0 = rooted). */
  readonly speed: number;
  readonly bodyRadius: number;
  readonly hurtRadius: number;
  /** Players this close to the boss (and inside the leash) get attacked. */
  readonly aggroRadius: number;
  /** Max distance from home before it gives up and walks back; also where it looks for targets. */
  readonly leashRadius: number;
  /** Ticks of telegraph before the attack lands. */
  readonly windupTicks: number;
  readonly cooldownTicks: number;
  /** Announcement colour. */
  readonly color: number;
}

/** Leaping slam: crouch, jump onto the spot where the target stood, crush everything in `slamRadius`. */
export const PUMPKIN_KING = {
  kind: BossKind.Pumpkin,
  name: 'Pumpkin King',
  maxHp: 660,
  speed: 30,
  bodyRadius: 10,
  hurtRadius: 14,
  aggroRadius: 80,
  leashRadius: 110,
  /** Leaps when its target's edge is this close; otherwise walks towards it. */
  leapRange: 90,
  /** Whole slam: crouch, then airborne for the last `airTicks`. */
  windupTicks: ticks(0.85),
  airTicks: ticks(0.4),
  /** Everything hostile within this radius of the landing spot (edge distance) is hit. */
  slamRadius: 30,
  slamDamage: 36,
  /** ...and shoved straight out of the ring (ADR-0036; no wall slam: the boss hurts enough). */
  slamKnock: { speed: 190, ticks: 6, decay: 0.78, slam: false } satisfies KnockDef,
  cooldownTicks: ticks(1.4),
  color: 0xf77622,
} as const;

/**
 * Rooted carnivorous plant: rears back (telegraph) and spits a fan of slow thorn seeds at its target
 * (SEED_SPIT), every `burstEvery`-th attack a ring all around; bites whatever stands right next to it.
 */
export const SNAPDRAGON = {
  kind: BossKind.Snapdragon,
  name: 'Snapdragon',
  maxHp: 570,
  speed: 0,
  bodyRadius: 11,
  hurtRadius: 14,
  aggroRadius: 150,
  /**
   * Rooted, so the leash is only how far it hunts whoever hurts it: past every common gun's reach (the Rifle's 320), or
   * a Pistol at 200 px farmed it for free (was 160).
   */
  leashRadius: 340,
  windupTicks: ticks(0.7),
  cooldownTicks: ticks(1.2),
  /** Seeds in an aimed fan and the fan's total width (radians). */
  volley: 5,
  volleySpread: 0.8,
  burstEvery: 3,
  /** Seeds in the ring. */
  burst: 14,
  /** Bites targets whose edge is this close. */
  biteRange: 14,
  biteDamage: 30,
  biteCooldownTicks: ticks(0.8),
  color: 0x63c74d,
} as const;

/**
 * Armoured beetle: lowers its horn (telegraph, aimed at where you stand), then charges in a straight line,
 * goring everything it runs through once. Ramming a wall stuns it — and a stunned beetle takes extra damage.
 */
export const RHINO_BEETLE = {
  kind: BossKind.Beetle,
  name: 'Rhino Beetle',
  maxHp: 690,
  speed: 28,
  bodyRadius: 11,
  hurtRadius: 13,
  aggroRadius: 90,
  /** Wide: a charge (~240 px) must not carry it out of its leash, or every charge would end in a heal. */
  leashRadius: 250,
  /** Starts a charge when its target's edge is this close. */
  chargeRange: 150,
  windupTicks: ticks(0.8),
  chargeSpeed: 220,
  chargeTicks: ticks(1.1),
  chargeDamage: 42,
  /** Extra reach of the gore around its body while charging. */
  chargeReach: 6,
  stunTicks: ticks(1.8),
  stunnedDamageMultiplier: 1.5,
  cooldownTicks: ticks(1.2),
  color: 0xb55088,
} as const;

export type BossDef = typeof PUMPKIN_KING | typeof SNAPDRAGON | typeof RHINO_BEETLE;

/** Indexed by BossKind. */
export const BOSSES: readonly BossDef[] = [PUMPKIN_KING, SNAPDRAGON, RHINO_BEETLE];
// Every def must provide the shared shape.
const _shape: readonly BossBase[] = BOSSES;
void _shape;

export function bossDef(kind: number): BossDef {
  return BOSSES[kind] ?? PUMPKIN_KING;
}

// ─── Jungle power-ups ─────────────────────────────────────────────────────────────────────────────────

export const PowerupKind = { Speed: 0, Rage: 1, Shield: 2, Heal: 3 } as const;
export type PowerupKind = (typeof PowerupKind)[keyof typeof PowerupKind];

export interface PowerupDef {
  readonly kind: PowerupKind;
  readonly key: 'speed' | 'rage' | 'shield' | 'heal';
  readonly name: string;
  readonly desc: string;
  /** How long it lasts (0 = instant: the Heart Berry). */
  readonly ticks: number;
  readonly color: number;
}

/**
 * Power-ups are the classic jungle camps' prizes (ADR-0067; they grew on spots of their own before): the player who
 * clears a camp gets its creature's power-up (`CreepDef.drop`) at once — nothing to pick up. Buffs run out, are lost on
 * death and reset with the round. Speed is simulated by the shared movement system (predicted). The Heart Berry
 * (ADR-0019) heals `POWERUP.heal` at once.
 */
export const POWERUPS: readonly PowerupDef[] = [
  {
    kind: PowerupKind.Speed,
    key: 'speed',
    name: 'Mint Rush',
    desc: 'You run 40% faster.',
    ticks: ticks(10),
    color: 0x63c74d,
  },
  {
    kind: PowerupKind.Rage,
    key: 'rage',
    name: 'Chili Rage',
    desc: 'Your shots hit 50% harder.',
    ticks: ticks(25),
    color: 0xe43b44,
  },
  {
    kind: PowerupKind.Shield,
    key: 'shield',
    name: 'Leaf Shield',
    desc: 'Soaks up the next 60 damage you take.',
    ticks: ticks(15),
    color: 0xe4a672,
  },
  {
    kind: PowerupKind.Heal,
    key: 'heal',
    name: 'Heart Berry',
    desc: 'Heals 60 health at once.',
    ticks: 0,
    color: 0xf6757a,
  },
];

export const POWERUP = {
  speedMultiplier: 1.4,
  rageMultiplier: 1.5,
  shieldHp: 60,
  /** Health the Heart Berry gives back (capped at full). */
  heal: 60,
} as const;

export function powerupById(kind: number): PowerupDef {
  return POWERUPS[kind] ?? (POWERUPS[0] as PowerupDef);
}

/**
 * A gun that can lie on the ground: a card out of a broken crate (ADR-0068), a gun a dead player dropped (ADR-0033), a
 * camp's or a range altar's (ADR-0021).
 */
export interface LootDef {
  readonly id: number;
  readonly key:
    | 'thornfire'
    | 'longstem'
    | 'whacker'
    | 'burner'
    | 'rocket'
    | 'laser'
    | 'nuke'
    | 'freeze'
    | 'grenade'
    | 'pollinator'
    | 'rail'
    | 'katana'
    | 'fist'
    | 'firefly'
    | 'hive'
    | 'saw'
    | 'shuriken'
    | 'toxic'
    | 'shotgun'
    | 'tornado'
    | 'abduct'
    | 'sword'
    | 'dart'
    | 'cupid'
    | 'dubstep'
    | 'firework'
    | 'mecha'
    | 'demon'
    | 'wand'
    | 'alien'
    | 'rc'
    | 'monster'
    | 'firebow'
    | 'shield'
    | 'airstrike'
    | 'saber'
    | 'scythe'
    | 'hammer'
    | 'trident'
    | 'tank'
    | 'unicorn'
    | 'ducks';
  /** Its gun (content weapon id). */
  readonly weapon: number;
  /** How often a crate's card holds it, relative to the others (0: never). */
  readonly weight: number;
  /** Dealt only where a battle royale is played (the Hive Launcher). */
  readonly royaleOnly?: true;
}

/**
 * A crate gun's weight: equal odds for every gun, the atom bomb included (ADR-0025; the newest were four times as likely
 * for a while, ADR-0037, until ADR-0039 evened them out again).
 */
const GUN_WEIGHT = 10;

/**
 * Guns only come out of crates (ADR-0010; as cards since ADR-0068), every gun as likely as any other (ADR-0025,
 * ADR-0039; rarity still sets their colour and power). No gold bags since ADR-0068. Weight 0 never drops
 * (the Pollinator: only the shooting range's altar holds one).
 */
export const LOOT: readonly LootDef[] = [
  { id: 0, key: 'thornfire', weapon: THORNFIRE.id, weight: GUN_WEIGHT },
  { id: 1, key: 'longstem', weapon: LONGSTEM.id, weight: GUN_WEIGHT },
  { id: 2, key: 'whacker', weapon: WHACKER.id, weight: GUN_WEIGHT },
  { id: 3, key: 'burner', weapon: WEED_BURNER.id, weight: GUN_WEIGHT },
  { id: 4, key: 'rocket', weapon: ROOT_ROCKET.id, weight: GUN_WEIGHT },
  { id: 5, key: 'laser', weapon: SUNBEAM.id, weight: GUN_WEIGHT },
  { id: 6, key: 'nuke', weapon: ATOM_BOMB.id, weight: GUN_WEIGHT },
  { id: 7, key: 'freeze', weapon: FROSTBITE.id, weight: GUN_WEIGHT },
  // Retired from every mode (feedback).
  { id: 8, key: 'grenade', weapon: CONE_LOBBER.id, weight: 0 },
  { id: 9, key: 'pollinator', weapon: POLLINATOR.id, weight: 0 },
  // The ADR-0035 guns, in crates and camps since ADR-0037.
  { id: 10, key: 'rail', weapon: CORN_RAIL.id, weight: GUN_WEIGHT },
  { id: 11, key: 'katana', weapon: PRUNER.id, weight: GUN_WEIGHT },
  { id: 12, key: 'fist', weapon: VINE_FIST.id, weight: 0 },
  // ADR-0078.
  { id: 13, key: 'firefly', weapon: FIREFLY.id, weight: GUN_WEIGHT },
  { id: 14, key: 'hive', weapon: BEEHIVE.id, weight: GUN_WEIGHT, royaleOnly: true },
  { id: 15, key: 'saw', weapon: BUZZSAW.id, weight: GUN_WEIGHT },
  { id: 16, key: 'shuriken', weapon: SHURIKEN.id, weight: GUN_WEIGHT },
  { id: 17, key: 'toxic', weapon: SLUDGE.id, weight: GUN_WEIGHT },
  // ADR-0083.
  { id: 18, key: 'shotgun', weapon: SHOTGUN.id, weight: GUN_WEIGHT },
  { id: 19, key: 'tornado', weapon: TORNADO.id, weight: 0 },
  // Archived (ADR-0099): never in a crate.
  { id: 20, key: 'abduct', weapon: ABDUCTOR.id, weight: 0 },
  { id: 21, key: 'sword', weapon: BERSERKER.id, weight: GUN_WEIGHT },
  { id: 22, key: 'dart', weapon: POISON_DART.id, weight: GUN_WEIGHT },
  // Archived (ADR-0099): never in a crate.
  { id: 23, key: 'cupid', weapon: CUPID_BOW.id, weight: 0 },
  { id: 24, key: 'dubstep', weapon: DUBSTEP.id, weight: GUN_WEIGHT },
  { id: 25, key: 'firework', weapon: FIREWORK.id, weight: GUN_WEIGHT },
  // A boss's reward since ADR-0108 (`BossBase.transform`): never in a crate.
  { id: 26, key: 'mecha', weapon: MECHA.id, weight: 0 },
  { id: 27, key: 'demon', weapon: GRIMOIRE.id, weight: 0 },
  { id: 28, key: 'wand', weapon: WAND.id, weight: GUN_WEIGHT },
  { id: 29, key: 'alien', weapon: ALIEN_ATTACK.id, weight: GUN_WEIGHT },
  // Archived: keep ids, never deal these guns.
  { id: 30, key: 'rc', weapon: RC_TRACTOR.id, weight: 0 },
  // A boss's reward since ADR-0108: never in a crate.
  { id: 31, key: 'monster', weapon: MONSTER.id, weight: 0 },
  // ADR-0109; retired from every mode (feedback).
  { id: 32, key: 'firebow', weapon: FIRE_BOW.id, weight: 0 },
  { id: 33, key: 'shield', weapon: SHIELD.id, weight: GUN_WEIGHT },
  // ADR-0159. The Tank and the Unicorn are transformations in a crate: taken, they go over the gun you hold.
  { id: 34, key: 'airstrike', weapon: AIR_STRIKE.id, weight: 0 },
  { id: 35, key: 'saber', weapon: LIGHTSABER.id, weight: 0 },
  { id: 36, key: 'scythe', weapon: SCYTHE.id, weight: 0 },
  { id: 37, key: 'hammer', weapon: HAMMER.id, weight: GUN_WEIGHT },
  { id: 38, key: 'trident', weapon: TRIDENT.id, weight: 0 },
  { id: 39, key: 'tank', weapon: TANK.id, weight: GUN_WEIGHT },
  { id: 40, key: 'unicorn', weapon: UNICORN.id, weight: GUN_WEIGHT },
  { id: 41, key: 'ducks', weapon: DUCKS.id, weight: 0 },
];

export function lootById(id: number): LootDef {
  const l = LOOT[id];
  if (!l) throw new Error(`Unknown loot id ${id}`);
  return l;
}

/**
 * The loot holding weapon `weapon` (a content weapon id; a battle royale's own gun, ADR-0189, its stand-in's: `baseGun`),
 * if any crate can hold it.
 */
export const lootForWeapon = (weapon: number): LootDef | undefined => {
  const base = baseGun(weapon);
  return LOOT.find((l) => l.weapon === base);
};

/** A tier of a battle royale's chest guns (ADR-0216): its name, its colour on the ground, its guns, its odds. */
export interface RoyaleTier {
  readonly name: string;
  /** Its guns' glow and rim where they lie, and their names over them (hex, the palette's). */
  readonly color: number;
  readonly guns: readonly LootDef['key'][];
  /** Per cent of chests that deal one of its guns: up top, underground. */
  readonly top: number;
  readonly under: number;
}

/**
 * A battle royale's chest guns by tier (ADR-0216, the user's three): a chest deals a tier at its odds, then one of its
 * guns, each as likely — Common (grey: the Rifle, the Shotgun, the Homing Gun) about 70%, Uncommon (blue: the Sniper,
 * the Freeze Gun, the Dubstep Gun) 29%, Epic (gold: the Bazooka, the Minigun, the Flamethrower, the Poison Darts) 1%;
 * underground (ADR-0196 amended: the tunnels' reward for their dark and their flood) Uncommon and Epic twice as often.
 * Common's share is less the Monster Tonic's (`ROYALE_LOOT.monster`): no tier's, a chest's rarest card. No Magic Wand
 * (gone from the battle royale).
 */
export const ROYALE_TIERS: { readonly common: RoyaleTier; readonly uncommon: RoyaleTier; readonly epic: RoyaleTier } = {
  common: { name: 'Common', color: 0xc0cbdc, guns: ['thornfire', 'shotgun', 'firefly'], top: 69.5, under: 39 },
  uncommon: { name: 'Uncommon', color: 0x0099db, guns: ['longstem', 'freeze', 'dubstep'], top: 29, under: 58 },
  epic: { name: 'Epic', color: 0xfeae34, guns: ['rocket', 'whacker', 'burner', 'dart'], top: 1, under: 2 },
};

/** The battle royale tier crate gun `key` is in (ADR-0216), if any. */
export const royaleTierOf = (key: string): RoyaleTier | undefined =>
  Object.values(ROYALE_TIERS).find((t) => t.guns.includes(key as LootDef['key']));

/**
 * A battle royale's chests (ADR-0189, by tier since ADR-0216: `ROYALE_TIERS`): a chest drops `cards` gun (ADR-0190: one,
 * no pick of three), the Monster Tonic `monster` per cent of chests (ADR-0202: up top, underground), and no other gun at
 * all. The Minigun and the Homing Gun are the battle royale's own (`royaleGun`). Never the Pistol: everyone holds one
 * from the start (`ROYALE_PISTOL`). The weapon altars hold `guns`, every tier's, Common first (a stone altar's guardian
 * drops an Epic one, ADR-0215). Beside its gun a chest spills one secondary `secondary` of the time
 * (`CHEST_SECONDARIES` says which).
 */
export const ROYALE_LOOT: {
  readonly guns: readonly LootDef['key'][];
  readonly monster: { readonly top: number; readonly under: number };
  readonly cards: number;
  readonly secondary: number;
} = {
  guns: Object.values(ROYALE_TIERS).flatMap((t) => t.guns),
  monster: { top: 0.5, under: 1 },
  cards: 1,
  secondary: 0.3,
};

/**
 * How often a battle royale's chest deals crate gun `l` (`under`: underground), as a weight: its tier's per cent split
 * evenly among its guns (ADR-0216), the Monster Tonic its own, none for the rest; × 6000, whole for tiers of two to four.
 */
const royaleWeight = (l: LootDef, under: boolean): number => {
  const pct = (o: { top: number; under: number }) => (under ? o.under : o.top) * 6000;
  if (l.key === 'monster') return Math.round(pct(ROYALE_LOOT.monster));
  const t = royaleTierOf(l.key);
  return t ? Math.round(pct(t) / t.guns.length) : 0;
};

/**
 * The guns on a broken crate's `n` cards (ADR-0068): each drawn by the crates' weights from the guns not drawn yet, card
 * `i` with the 32-bit hash `roll(i)`; one strike (the atom bomb, an Alien Attack: ADR-0099) at most, none once one has been dealt this round (`noBomb`,
 * ADR-0080); the Hive Launcher only where `royale`, a battle royale's chest `under` the ground by its own odds.
 */
export function crateCards(
  n: number,
  roll: (i: number) => number,
  noBomb = false,
  royale = false,
  under = false,
): LootDef[] {
  const left = LOOT.filter(
    (l) =>
      (royale ? royaleWeight(l, under) > 0 : l.weight > 0) &&
      !(noBomb && weaponById(l.weapon).strike) &&
      (royale || !l.royaleOnly),
  );
  const weight = (l: LootDef) => (royale ? royaleWeight(l, under) : l.weight);
  const out: LootDef[] = [];
  for (let i = 0; i < n && left.length > 0; i++) {
    // None left that may be dealt (a battle royale deals five guns at most).
    const total = left.reduce((s, l) => s + weight(l), 0);
    if (total <= 0) break;
    let r = roll(i) % total;
    let k = 0;
    while (r >= weight(left[k] as LootDef)) r -= weight(left[k++] as LootDef);
    const [card] = left.splice(k, 1) as [LootDef];
    out.push(card);
    if (weaponById(card.weapon).strike)
      for (let j = left.length - 1; j >= 0; j--) if (weaponById((left[j] as LootDef).weapon).strike) left.splice(j, 1);
  }
  return out;
}

/** Guns dropped by the fallen. */
export const DROPS = {
  /**
   * A player killed holding a crate gun drops it (ADR-0033): a card of its own (ADR-0072) flies this far out of the body
   * along the killing blow (px; away from the killer) and lies there this long — anyone may take it, with a fresh clock.
   */
  deathToss: 20,
  deathLifetimeTicks: ticks(20),
} as const;

/** The kinds of breakable crate (ADR-0068; the `crate` entity's `variant`): a battle royale's mini chest too (ADR-0189). */
/** A crate's kind (ADR-0068): an airdrop (a battle royale's chest), an apple crate, the range's truck, a mini chest, a
 * battle royale's hot-air balloon (ADR-0199: in the sky, then its basket on the ground), a bounty's gold chest (ADR-0229:
 * a chest's size, an Epic gun in it). */
export const CrateKind = { Airdrop: 0, Apple: 1, Truck: 2, Mini: 3, Balloon: 4, Gold: 5 } as const;
export type CrateKind = (typeof CrateKind)[keyof typeof CrateKind];

/**
 * Crates you break (ADR-0068). A landed airdrop is a solid crate that anyone's shots, blows and blasts break — never a
 * minion's, a turret's, a boss's or a creep's — in about 2 s of the Pistol. Broken, it bursts into `cards` cards in a row
 * under it (`cardDrop` px below, `cardGap` px apart, ADR-0072; they fanned out towards the breaker before), each a
 * different crate gun, lying `cardTicks`. A player takes one within `takeReach` px of it (the `take` order: right
 * click; 64 px before ADR-0072); the others go with it.
 * Every gun on the ground is such a card (ADR-0072). A chest standing on a chest spot (the shooting range's `$`) grows
 * back `regrowTicks` after it breaks.
 */
export const CRATE = {
  hp: 100,
  bodyRadius: 7,
  hurtRadius: 9,
  cards: 3,
  cardDrop: 30,
  cardGap: 40,
  cardTicks: ticks(30),
  takeReach: 112,
  regrowTicks: ticks(3),
} as const;

/**
 * The apple crates (`c` on any map, ADR-0068): solid cover that breaks and heals the player who broke it `heal` at once
 * (capped at full, like the Heart Berry; ADR-0068 amended), and grows back `regrowTicks` later, once nothing stands
 * where it goes.
 */
export const APPLE_CRATE = {
  hp: 60,
  bodyRadius: 7,
  hurtRadius: 8,
  heal: 20,
  regrowTicks: ticks(45),
} as const;

/**
 * The shooting range's old pickup truck parked by the cottage (`x`, ADR-0100): it is wrecked by `hp` of player fire and
 * blows up — `damage` at its centre, half at the rim of `radius`, thrown by `knock`, the wrecker's blast (their team
 * spared) — and is back `regrowTicks` later. A crate to the sim: a round static body.
 */
export const TRUCK = {
  hp: 240,
  bodyRadius: 11,
  hurtRadius: 13,
  radius: 80,
  damage: 110,
  knock: { speed: 360, ticks: 10, decay: 0.82, slam: true },
  regrowTicks: ticks(10),
} as const;

/**
 * A battle royale's mini chest (`&`, ADR-0189): a small chest that breaks in a moment and bursts into `drops` pickups
 * (`PICKUP`), each a grenade `grenadeOdds` of the time (ADR-0194 amended), C4 `c4Odds` (ADR-0201), an Ejector Seat
 * `ejectorOdds` (ADR-0218), beacons `beaconOdds` (ADR-0220 amended), a gramophone `gramophoneOdds` (ADR-0221), else a
 * shield shard `shieldOdds` of the time, else a small heal. Once a round, like the chests.
 */
export const MINI_CHEST = {
  hp: 40,
  bodyRadius: 5,
  hurtRadius: 7,
  regrowTicks: ticks(60),
  drops: 3,
  grenadeOdds: 0.2,
  c4Odds: 0.08,
  ejectorOdds: 0.01,
  beaconOdds: 0.02,
  gramophoneOdds: 0.006,
  shieldOdds: 0.65,
  /** Its pickups land this far from it (px), fanned out under it. */
  spill: 14,
} as const;

/**
 * A battle royale's hot-air balloon (ADR-0199): `at` ticks into the round, one each, a balloon drifts in over the sea at
 * `speed` px/s on a line across the island through the next circle (within `scatter` of its radius of its middle, the
 * way the world's secret says), its envelope's middle `height` px over its shadow, shot at there (`hurt` px round, `hp`
 * of anyone's fire). Popped, its basket falls `fallTicks` onto the clear ground nearest under it: a chest that opens as
 * any other does (`ROYALE.open`) and holds the crypt key (ADR-0215; the Bazooka before). Not popped, it drifts off over
 * the sea again. It comes in and goes `sea` px off the map's edges.
 */
export const BALLOON = {
  at: [ticks(50), ticks(130)],
  speed: 24,
  height: 122,
  hurt: 26,
  hp: 360,
  fallTicks: ticks(1.4),
  scatter: 0.5,
  sea: 40,
} as const;

/**
 * A crate kind's health, body and hurt radii and regrowth (ADR-0068, ADR-0100); a balloon's (ADR-0199) its basket's on
 * the ground, a chest's, and its health in the sky.
 */
export const crateDef = (kind: number): { hp: number; bodyRadius: number; hurtRadius: number; regrowTicks: number } =>
  kind === CrateKind.Apple
    ? APPLE_CRATE
    : kind === CrateKind.Truck
      ? TRUCK
      : kind === CrateKind.Mini
        ? MINI_CHEST
        : kind === CrateKind.Balloon
          ? { ...CRATE, hp: BALLOON.hp }
          : CRATE;

/**
 * What a pickup gives (ADR-0189; the `pickup` entity's `kind`): health back, a shield on top of it, grenades (ADR-0194
 * amended: a round starts with none), C4 (ADR-0201), the crypt key (ADR-0202: a balloon's basket's, ADR-0215, or a
 * holder's, fallen with them), an Ejector Seat (ADR-0218), beacons (ADR-0220 amended), Wind-up gramophones (ADR-0221) or
 * a fishing rod (ADR-0229: `amount` its baits).
 */
export const PickupKind = {
  Heal: 0,
  Shield: 1,
  Grenade: 2,
  C4: 3,
  Key: 4,
  Ejector: 5,
  Beacon: 6,
  Gramophone: 7,
  Rod: 8,
} as const;
export type PickupKind = (typeof PickupKind)[keyof typeof PickupKind];

/** The explosive a pickup of `kind` holds (content `Explosive`, ADR-0201), or -1: none. */
export const explosiveOf = (kind: number): number =>
  kind === PickupKind.Grenade
    ? Explosive.Grenade
    : kind === PickupKind.C4
      ? Explosive.C4
      : kind === PickupKind.Ejector
        ? Explosive.Ejector
        : kind === PickupKind.Beacon
          ? Explosive.Beacon
          : kind === PickupKind.Gramophone
            ? Explosive.Gramophone
            : kind === PickupKind.Rod
              ? Explosive.Rod
              : -1;

/** The pickup that holds explosive `kind` (ADR-0201): what a gardener swapping it away leaves lying. */
export const pickupOf = (kind: number): PickupKind =>
  kind === Explosive.C4
    ? PickupKind.C4
    : kind === Explosive.Ejector
      ? PickupKind.Ejector
      : kind === Explosive.Beacon
        ? PickupKind.Beacon
        : kind === Explosive.Gramophone
          ? PickupKind.Gramophone
          : kind === Explosive.Rod
            ? PickupKind.Rod
            : PickupKind.Grenade;

/**
 * Pickups (ADR-0189): what a battle royale's gardener walks over to take. A heal (`heal` on the map's health spots, its
 * apple crate spots: they stand there instead; `small` out of a mini chest) or a shield shard (`shard`) on top of the
 * shield they have, up to `shieldMax`: a shield stays until it is shot off. The nearest standing gardener within `reach` px
 * (edge to edge, a tile: 6 before ADR-0193's amendment; no wall between) who needs it takes it: one at full health leaves a heal lying, one with a full shield a shard. Out of a
 * mini chest one flies `landTicks` before it can be taken; a broken thing (a pot, a barrel, a crate: ADR-0188) hides one
 * `thingOdds` per cent of the time (`thing` of either). Grenades (ADR-0194 amended): `grenades` of them lie on a health
 * spot `grenadeSpots` of the time instead of its heal (each spot drawn each round), and spill out of a mini chest; one
 * who carries `GRENADE.count` already leaves them lying. C4 (ADR-0201): `c4` charges on a health spot `c4Spots` of the
 * time; one who carries grenades leaves it lying for SPACE to swap them (and the other way round). An Ejector Seat
 * (ADR-0218): `ejector` of them on a health spot `ejectorSpots` of the time, swapped as C4 is; beacons (ADR-0220
 * amended): `beacons` on one `beaconSpots` of the time, the same; Wind-up gramophones (ADR-0221): `gramophones` on one
 * `gramophoneSpots` of the time, the same.
 */
export const PICKUP = {
  heal: 25,
  grenades: 2,
  grenadeSpots: 0.25,
  c4: 2,
  c4Spots: 0.1,
  ejector: 1,
  ejectorSpots: 0.04,
  beacons: 2,
  beaconSpots: 0.08,
  gramophones: 1,
  gramophoneSpots: 0.02,
  small: 15,
  shard: 15,
  shieldMax: 100,
  reach: 16,
  landTicks: ticks(0.4),
  thingOdds: 20,
  thing: 10,
} as const;

/**
 * Which secondary a battle royale's chest spills beside its gun (`ROYALE_LOOT.secondary`), by weight: the rarer ones
 * (beacons, an Ejector Seat, a gramophone) about twice as likely as out of a mini chest (`MINI_CHEST`: grenades 63%, C4
 * 25%, beacons 6%, an Ejector Seat 3%, a gramophone 2%).
 */
export const CHEST_SECONDARIES: readonly {
  readonly kind: PickupKind;
  readonly amount: number;
  readonly weight: number;
}[] = [
  { kind: PickupKind.Grenade, amount: PICKUP.grenades, weight: 45 },
  { kind: PickupKind.C4, amount: PICKUP.c4, weight: 30 },
  { kind: PickupKind.Beacon, amount: PICKUP.beacons, weight: 12 },
  { kind: PickupKind.Ejector, amount: PICKUP.ejector, weight: 8 },
  { kind: PickupKind.Gramophone, amount: PICKUP.gramophones, weight: 5 },
];

/** Damage boost per level of Zombie Nights' Damage card. */
export const DAMAGE_BOOST_PER_LEVEL = 0.2;

/** Weapon damage after `level` damage boosts. */
export const boostedDamage = (base: number, level: number): number =>
  Math.round(base * (1 + DAMAGE_BOOST_PER_LEVEL * level));
